import { useState } from "react";
import { Button, FieldLabel, cx, isApiError } from "@amber/ui";
import { WORD_REASON_CATEGORIES, type EvidenceRecord, type TurnView } from "../../contracts/index";
import { OpenReports, communicationOf, type Editing } from "./Editing";

function saveErrorText(e: unknown): string {
  if (!isApiError(e)) return "Couldn't save the correction";
  if (e.code === "conflict" && e.current !== null) {
    return `${e.changedBy ?? "Someone"} changed this passage meanwhile. Reload the turn and correct the word again.`;
  }
  return e.message;
}

export function WordCorrection({
  record,
  turn,
  position,
  editing,
  onDone,
}: {
  record: EvidenceRecord;
  turn: TurnView;
  position: number;
  editing: Editing;
  onDone: (message: string) => void;
}) {
  const word = turn.words[position];
  const [value, setValue] = useState(word?.text ?? "");
  const [reason, setReason] = useState("");
  const [resolves, setResolves] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const com = communicationOf(record, turn);
  const [base] = useState(() =>
    com?.version && turn.unit_id ? { revisionId: com.version.revision_id, unitId: turn.unit_id } : null,
  );
  const problems = record.problems.filter((p) => turn.problems.includes(p.id));
  if (!word) return null;
  const apply = async () => {
    if (!com || !base) return;
    if (value.trim() === word.text) {
      setError("The word is unchanged");
      return;
    }
    if (reason.trim().length === 0) {
      setError("Pick or type a reason");
      return;
    }
    setBusy(true);
    try {
      await editing.save({
        communicationId: com.id,
        baseRevisionId: base.revisionId,
        unitId: base.unitId,
        reason: reason.trim(),
        changes: [{ kind: "word", position, text: value.trim() }],
        resolves,
      });
      onDone(`Corrected “${word.text}” to “${value.trim()}”. Checks rerun.`);
    } catch (e) {
      setError(saveErrorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="mt-2 border-t border-divider pt-2"
      onSubmit={(e) => {
        e.preventDefault();
        void apply();
      }}
    >
      <FieldLabel htmlFor={`word-${String(position)}`}>Correct to</FieldLabel>
      <input
        id={`word-${String(position)}`}
        className="h-8 w-full rounded-md border border-divider bg-bg px-2 text-base"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setError(null);
        }}
      />
      <div role="group" aria-label="Reason" className="mt-2 flex flex-wrap gap-1">
        {WORD_REASON_CATEGORIES.map((r) => (
          <button
            key={r}
            type="button"
            aria-pressed={reason === r}
            onClick={() => {
              setReason(r);
              setError(null);
            }}
            className={cx(
              "rounded-full border px-2 py-0.5 text-md",
              reason === r ? "border-link bg-accent-soft text-link" : "border-divider",
            )}
          >
            {r}
          </button>
        ))}
      </div>
      <input
        aria-label="Or type a reason"
        placeholder="Or type a reason"
        className="mt-1.5 h-8 w-full rounded-md border border-divider bg-bg px-2 text-md"
        value={WORD_REASON_CATEGORIES.includes(reason as (typeof WORD_REASON_CATEGORIES)[number]) ? "" : reason}
        onChange={(e) => {
          setReason(e.target.value);
          setError(null);
        }}
      />
      <OpenReports problems={problems} picked={resolves} onPick={setResolves} />
      {error ? (
        <p role="alert" className="m-0 mt-1.5 text-md text-attention">
          {error}
        </p>
      ) : null}
      <div className="mt-2 flex justify-end">
        <Button size="sm" type="submit" variant="primary" aria-busy={busy || undefined}>
          Apply
        </Button>
      </div>
    </form>
  );
}
